import {
  AnswerMode,
  PromptExample,
} from "../features/answer-modes/AnswerModeContainer/AnswerMode";

export const enum RetrievalMode {
  Hybrid = "hybrid",
  Vectors = "vectors",
  Text = "text",
}

export type ChatAppRequestOverrides = {
  retrieval_mode?: RetrievalMode;
  semantic_ranker?: boolean;
  semantic_captions?: boolean;
  exclude_category?: string;
  top?: number;
  prompt_template?: string;
  prompt_template_prefix?: string;
  prompt_template_suffix?: string;
  suggest_followup_questions?: boolean;
  use_oid_security_filter?: boolean;
  use_groups_security_filter?: boolean;
};

export type ResponseMessage = {
  content: string;
  role: string;
};

export interface ProcessStep {
  type: string;
  label: string;
  details: string; // e.g., the actual query used or number of docs found
  status: "pending" | "in_progress" | "completed";
  step_id?: string;
}

export type ResponseContext = {
  thoughts: string | null;
  data_points: string[];
  process_steps?: ProcessStep[]; // <--- ADD THIS LINE
  visualizations?: Record<
    string,
    { language: string; code: string; description: string; validated: boolean }
  >;
};

export type ResponseChoice = {
  index: number;
  message: ResponseMessage;
  context: ResponseContext;
  session_state: any;
  timestamp: number;
};

export type ChatAppResponseOrError = {
  choices: ResponseChoice[];
  error?: string;
};

export type ChatAppResponse = {
  choices: ResponseChoice[];
  feedback?: Feedback;
};

export type ChatAppRequestContext = {
  overrides?: ChatAppRequestOverrides;
};

export type ModelParameters = {
  answerMode: string;
};

export type SiteMappingOption = {
  mfilesSite: string;
  mfilesSiteName: string;
  city: string;
};

export interface ChatAppRequest {
  messages: ResponseMessage[];
  session_state: string;
  answer_mode: AnswerMode;
  model: string;
  tools: string[];
  selectedSites?: string[];
  fileNames?: string[];
  state_snapshot?: Record<string, any> | null; // ADD
}

export type SummarizeChatRequest = {
  messages: ResponseMessage[];
};

export type ToolInfo = {
    id: string;
    name: string;
    description: string;
    icon: string;
    supported_models: string[];
};

export type Config = {
  enabledUseCases: {
    [key: string]: {
      showUserUpload: boolean;
    };
  };
  modelSettingsAllowed: {
    [key: string]: boolean;
  };
  availableModels: { 
    [key: string]: string[];
  };
  availableTools: {
    [key: string]: ToolInfo[];
  };
  siteMappings: {
    [key: string]: SiteMappingOption[];
  };

};

export type DocumentAPIResponse = {
  message?: string;
};

export type FileUpdate = {
  node: string;
  message: string;
  step: number;
  total_steps: number;
};

export type ProgressCallback = (update: FileUpdate) => void;

export enum Feedback {
  Neutral = "neutral",
  Positive = "positive",
  Negative = "negative",
  MissingCitation = "missing_citation",
  WrongCitation = "wrong_citation",
  OutOfScope = "out_of_scope",
  InaccurateOrIrrelevant = "inaccurate_or_irrelevant",
  OtherUnhelpful = "other_unhelpful",
  SlowAppPerformance = "slow_app",
}

export interface GenericRagStateData {
  ragState: boolean;
}

export interface BadgeVisibilityContextType {
  isBadgeVisible: boolean;
  updateBadgeVisibility: () => void;
}
// --- DATA MODEL FOR BACKEND ---
// This interface represents the data we are preparing to send

export interface PromptAiGeneratorRequest {
  inputText: string;
  outputText: string;
  inputFiles: File[];
  outputFiles: File[];
}

// The response from the backend after analyzing the files/text
export interface PromptResponse {
  // The Identity & Content
  title: string;
  roleDefinition: string;
  taskDescription: string;
  outputFormat: string;

  // The Few-Shot Examples
  examples: PromptExample[];

  // May include extended tiers from newer models (e.g. xhigh/max).
  suggestedReasoning: "none" | "low" | "medium" | "high" | "xhigh" | "max";
}

export interface ConversationPromptResponse {
  title: string;
  roleDefinition: string;
  taskDescription: string;
  outputFormat: string;
  examples: PromptExample[];
  suggestedReasoning: "none" | "low" | "medium" | "high" | "xhigh" | "max";
}

export interface PromptGenerationUpdate {
  node: string;
  message: string;
  step: number;
  total_steps: number;
  meta_data?: any;
  result?: PromptResponse; // The final result will appear here in the last chunk
}
export type Answers = [user: string, response: ChatAppResponse][];
// define types for metadata and answers

export type HistoryMetaData = {
  id: string;
  title: string;
  timestamp: number;
  useCase: string;
  // New fields for UI
  messageCount: number;
  fileCount: number;
  imageCount: number;
};
export type FileProgressUpdate = {
  node: string;
  message: string;
  step: number;
  total_steps: number;
  meta_data?: {
    unique_storage_urls?: string[];
    blob_filepath?: string;
    file_name?: string;
    warning?: string;
    [key: string]: any;
  };
};
export interface ConversationToPromptRequest {
  messages: ResponseMessage[]; // [question, answer] pairs
}
